// src/components/messaging/TypingIndicator.tsx
import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface TypingIndicatorProps {
  userNames?: string[];
}

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
`;

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ userNames = [] }) => {
  const { t } = useTranslation();

  if (userNames.length === 0) return null;

  // Format typing text
  const getTypingText = (): string => {
    if (userNames.length === 1) {
      return t('messaging.isTyping', '{{name}} écrit...', { name: userNames[0] });
    }
    if (userNames.length === 2) {
      return t('messaging.areTyping', '{{name1}} et {{name2}} écrivent...', {
        name1: userNames[0],
        name2: userNames[1],
      });
    }
    return t('messaging.multipleTyping', 'Plusieurs personnes écrivent...');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 0.5,
        gap: 1,
      }}
    >
      {/* Animated dots */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'grey.400',
              animation: `${bounce} 1.4s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </Box>

      {/* Text */}
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {getTypingText()}
      </Typography>
    </Box>
  );
};

export default TypingIndicator;
